import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Select, Button, Collapse, InputNumber, Switch, Table,
  Card, Row, Col, message, Spin, Empty, Slider,
} from 'antd';
import { PlayCircleOutlined, TrophyOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import PageContainer from '@/components/PageContainer';
import { fetchBacktestReadyMonths } from '@/api/mediumTermBacktest';
import { apiClient, localApiClient } from '@/api/client';

interface OptimizeResult {
  score: number;
  params: Record<string, unknown>;
  summary: Record<string, number>;
}

export default function StrategyOptimizerPage() {
  const [months, setMonths] = useState<string[]>([]);
  const [month, setMonth] = useState('');
  const [localCompute, setLocalCompute] = useState(() => {
    try { return localStorage.getItem('optimizer_local') === '1'; } catch { return false; }
  });
  const [mode, setMode] = useState<'buyer' | 'seller'>('seller');
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<OptimizeResult[]>([]);
  const [, setProgress] = useState({ done: 0, total: 0 });
  const runVersionRef = useRef(0);
  const [redline, setRedline] = useState(0.38);
  const [flatBenchmarkRatio, setFlatBenchmarkRatio] = useState(0.38);

  // Objective weights
  const [wSortino, setWSortino] = useState(0);
  const [wPnl, setWPnl] = useState(100);
  const [wWR, setWWR] = useState(0);
  const [wCVaR, setWCVaR] = useState(0);
  const [wMWh, setWMWh] = useState(0);
  const [wMDD, setWMDD] = useState(0);

  // Search grid values (no UI to adjust — const for now)
  const searchL = [0.3, 0.5, 0.7];
  const searchX0 = [5, 10, 15, 20];
  const searchK = [0.1, 0.2, 0.3];
  const searchEntryThreshold = [5, 10, 15, 20, 30];
  const [searchSig, setSearchSig] = useState(true);  // true = both momentum+reversal
  const searchMaxPos = mode === 'buyer'
    ? [0.5, 1.0].filter((value) => value >= Math.max(0.45, redline, flatBenchmarkRatio))
    : [0.5, 1.0];
  const hasObjectiveWeight = wSortino + wPnl + wWR + wCVaR + wMWh + wMDD > 0;

  const clearResults = useCallback(() => {
    runVersionRef.current += 1;
    setRunning(false);
    setResults([]);
    setProgress({ done: 0, total: 0 });
  }, []);

  useEffect(() => {
    fetchBacktestReadyMonths().then(setMonths).catch(console.error);
  }, []);
  useEffect(() => {
    if (months.length > 0 && !month) {
      clearResults();
      setMonth(months[0]);
    }
  }, [months, month, clearResults]);

  const handleRun = useCallback(async () => {
    if (!month) { message.warning('请先选择交易月份'); return; }
    if (!hasObjectiveWeight) { message.warning('目标函数至少保留一个非零权重'); return; }
    clearResults();
    const runVersion = runVersionRef.current;
    setRunning(true);
    const payload = {
      trade_month: month,
      mode,
      redline: mode === 'buyer' ? redline : 0,
      flat_benchmark_ratio: mode === 'buyer' ? flatBenchmarkRatio : 0,
      weight_sortino: wSortino / 100,
      weight_avg_pnl: wPnl / 100,
      weight_win_rate: wWR / 100,
      weight_cvar: wCVaR / 100,
      weight_pnl_per_mwh: wMWh / 100,
      weight_min_mdd: wMDD / 100,
      search_L: searchL, search_x0: searchX0, search_k: searchK,
      search_entry_threshold: searchEntryThreshold,
      search_signal_normal: searchSig ? ['momentum', 'reversal'] : ['momentum'],
      search_max_pos: searchMaxPos,
    };
    console.log('=== 寻优请求 ===');
    console.log(JSON.stringify(payload, null, 2));
    try {
      const client = localCompute ? localApiClient : apiClient;
      const res = await client.post('/medium-term-backtest/optimize', payload);
      if (runVersion !== runVersionRef.current) return;
      console.log('=== 寻优响应 ===');
      console.log('status:', res.status, 'data keys:', Object.keys(res.data.data || {}));
      const data = res.data.data;
      const top10 = data.top10 || [];
      setResults(top10);
      setProgress({ done: data.total, total: data.total });
      message.success(`寻优完成，${data.total || 0} 组合`);
    } catch (e: unknown) {
      if (runVersion !== runVersionRef.current) return;
      console.error('=== 寻优失败 ===', e);
      const msg = e instanceof Error ? e.message : (e as { response?: { data?: { message?: string } } })?.response?.data?.message || '寻优失败';
      message.error(msg);
    } finally {
      if (runVersion === runVersionRef.current) setRunning(false);
    }
  }, [month, mode, redline, flatBenchmarkRatio, wSortino, wPnl, wWR, wCVaR, wMWh, wMDD, searchSig, localCompute, clearResults, hasObjectiveWeight]);

  const columns: ColumnsType<OptimizeResult> = [
    { title: '排名', key: 'rank', width: 60, render: (_: unknown, __: unknown, idx: number) => (
      <span style={{ fontWeight: idx === 0 ? 700 : 400, color: idx === 0 ? '#faad14' : '#666' }}>
        {idx === 0 ? <TrophyOutlined style={{ marginRight: 4 }} /> : null}#{idx + 1}
      </span>
    )},
    { title: '得分', dataIndex: 'score', key: 'score', width: 80, render: (v: number) => v.toFixed(4) },
    { title: '信号', key: 'signal', width: 70, render: (_: unknown, r: OptimizeResult) => r.params.signal_normal as string },
    { title: 'L', key: 'L', width: 60, render: (_: unknown, r: OptimizeResult) => String(r.params.L) },
    { title: 'x0', key: 'x0', width: 60, render: (_: unknown, r: OptimizeResult) => String(r.params.x0) },
    { title: 'k', key: 'k', width: 60, render: (_: unknown, r: OptimizeResult) => String(r.params.k) },
    { title: '开仓阈值', key: 'entry_threshold', width: 90, render: (_: unknown, r: OptimizeResult) => String(r.params.entry_threshold) },
    { title: '上限', key: 'max', width: 60, render: (_: unknown, r: OptimizeResult) => String(r.params.max_pos_limit) },
    {
      title: '相对基准收益', key: 'pnl', width: 110, align: 'right',
      render: (_: unknown, r: OptimizeResult) => `¥${(r.summary.alpha_pnl ?? 0).toFixed(0)}`,
    },
    {
      title: '胜率', key: 'wr', width: 70, align: 'right',
      render: (_: unknown, r: OptimizeResult) => `${((r.summary.macro_win_rate ?? 0) * 100).toFixed(0)}%`,
    },
    {
      title: 'Sortino', key: 'sortino', width: 80, align: 'right',
      render: (_: unknown, r: OptimizeResult) => (r.summary.sortino_ratio ?? 0).toFixed(1),
    },
    {
      title: 'CVaR95', key: 'cvar', width: 90, align: 'right',
      render: (_: unknown, r: OptimizeResult) => `¥${(r.summary.cvar_95 ?? 0).toFixed(0)}`,
    },
    {
      title: '相对基准度电收益', key: 'alpha_per_mwh', width: 130, align: 'right',
      render: (_: unknown, r: OptimizeResult) => `¥${(r.summary.alpha_per_traded_mwh ?? 0).toFixed(2)}`,
    },
    {
      title: '最大回撤', key: 'mdd', width: 90, align: 'right',
      render: (_: unknown, r: OptimizeResult) => `¥${(r.summary.avg_mdd ?? 0).toFixed(0)}`,
    },
  ];

  return (
    <PageContainer title="策略参数自动寻优">
      <div style={{ display: 'flex', gap: 16 }}>
        <Card size="small" style={{ width: 340, flexShrink: 0 }} styles={{ body: { padding: 12 } }}>
          <div style={{ marginBottom: 12 }}>
            <span style={{ fontWeight: 600, fontSize: 13 }}>交易月份</span>
            <Select size="small" style={{ width: '100%', marginTop: 4 }} value={month || undefined} onChange={(value) => {
              clearResults();
              setMonth(value);
            }}
              options={months.map((m) => ({ value: m, label: m }))} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <span style={{ fontWeight: 600, fontSize: 13 }}>视角</span>
            <Select size="small" style={{ width: '100%', marginTop: 4 }} value={mode} onChange={(value) => {
              clearResults();
              setMode(value);
            }}
              options={[{ value: 'buyer', label: '买方' }, { value: 'seller', label: '卖方' }]} />
          </div>
          {mode === 'buyer' && (
            <div style={{ marginBottom: 12 }}>
              <Row gutter={8}>
                <Col span={12}>
                  <span style={{ fontSize: 12 }}>月末持仓红线</span>
                  <InputNumber
                    aria-label="买方红线"
                    size="small"
                    min={0}
                    max={1}
                    step={0.01}
                    value={redline}
                    onChange={(value) => {
                      clearResults();
                      setRedline(value ?? 0.38);
                    }}
                    style={{ width: '100%', marginTop: 4 }}
                  />
                </Col>
                <Col span={12}>
                  <span style={{ fontSize: 12 }}>全月平铺基准</span>
                  <InputNumber
                    aria-label="全月平铺基准比例"
                    size="small"
                    min={0}
                    max={1}
                    step={0.01}
                    value={flatBenchmarkRatio}
                    onChange={(value) => {
                      clearResults();
                      setFlatBenchmarkRatio(value ?? 0.38);
                    }}
                    style={{ width: '100%', marginTop: 4 }}
                  />
                </Col>
              </Row>
            </div>
          )}
          <Collapse size="small" ghost items={[{
            key: 'objective', label: '目标函数权重',
            children: (
              <div>
                <Row gutter={[8, 4]}>
                  <Col span={24}><span style={{ fontSize: 11 }}>Sortino (风险调整收益)</span>
                    <Slider ariaLabelForHandle="Sortino权重" min={0} max={100} value={wSortino} onChange={(value) => { clearResults(); setWSortino(value); }} /></Col>
                  <Col span={24}><span style={{ fontSize: 11 }}>相对基准收益</span>
                    <Slider ariaLabelForHandle="相对基准收益权重" min={0} max={100} value={wPnl} onChange={(value) => { clearResults(); setWPnl(value); }} /></Col>
                  <Col span={24}><span style={{ fontSize: 11 }}>胜率</span>
                    <Slider ariaLabelForHandle="胜率权重" min={0} max={100} value={wWR} onChange={(value) => { clearResults(); setWWR(value); }} /></Col>
                  <Col span={24}><span style={{ fontSize: 11 }}>CVaR (尾部风险)</span>
                    <Slider ariaLabelForHandle="CVaR权重" min={0} max={100} value={wCVaR} onChange={(value) => { clearResults(); setWCVaR(value); }} /></Col>
                  <Col span={24}><span style={{ fontSize: 11 }}>相对基准度电收益</span>
                    <Slider ariaLabelForHandle="相对基准度电收益权重" min={0} max={100} value={wMWh} onChange={(value) => { clearResults(); setWMWh(value); }} /></Col>
                  <Col span={24}><span style={{ fontSize: 11 }}>最小化回撤</span>
                    <Slider ariaLabelForHandle="回撤权重" min={0} max={100} value={wMDD} onChange={(value) => { clearResults(); setWMDD(value); }} /></Col>
                </Row>
              </div>
            ),
          }, {
            key: 'search', label: '搜索空间',
            children: (
              <div>
                <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11 }}>信号方向:</span>
                  <Switch size="small" checked={searchSig} onChange={(value) => { clearResults(); setSearchSig(value); }}
                    checkedChildren="动量+反转" unCheckedChildren="仅动量" />
                </div>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>L (调仓幅度): {searchL.join(', ')}</div>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>x0 (放量中心): {searchX0.join(', ')}</div>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>k (斜率): {searchK.join(', ')}</div>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>开仓阈值 (元/MWh): {searchEntryThreshold.join(', ')}</div>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>持仓上限: {searchMaxPos.join(', ')}</div>
              </div>
            ),
          }]} />

          <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11 }}>本地计算</span>
            <Switch size="small" checked={localCompute} onChange={(v) => {
              clearResults();
              setLocalCompute(v);
              try { localStorage.setItem('optimizer_local', v ? '1' : '0'); } catch {}
            }} />
          </div>
          {!hasObjectiveWeight && (
            <div style={{ marginTop: 8, color: '#d46b08', fontSize: 12 }}>目标函数至少保留一个非零权重</div>
          )}
          <Button type="primary" icon={<PlayCircleOutlined />} loading={running} disabled={!month || !hasObjectiveWeight}
            onClick={handleRun} block size="large" style={{ marginTop: 8, height: 44 }}>
            {running ? `寻优中...` : (localCompute ? '💻 本地寻优' : '☁️ 云端寻优')}
          </Button>
        </Card>

        <div style={{ flex: 1 }}>
          {!results.length && !running ? (
            <Card><Empty description="配置参数后点击「开始寻优」" /></Card>
          ) : running ? (
            <Card><div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" tip="正在网格搜索..." /></div></Card>
          ) : (
            <Card title={`寻优结果 — Top ${results.length}`} size="small">
              <Table columns={columns} dataSource={results.map((r, i) => ({ ...r, key: i }))}
                size="small" bordered pagination={false} scroll={{ x: 900 }} />
            </Card>
          )}
        </div>
      </div>
    </PageContainer>
  );
}
